/** Where the bearer token comes from. Both paths end at an Entra ID access token audienced to this API (no application-specific password).
 *  Inside Teams: `authentication.getAuthToken()`, silent. Plain browser: MSAL redirect, usually a bounce with no typing.
 *  The silent path needs the Teams manifest's `webApplicationInfo` and an app registration exposing `access_as_user` with the Teams client ids pre-authorized (teams/manifest.json, teams/README.md). */
import { app, authentication } from '@microsoft/teams-js'

import {
  acquireApiToken,
  cachedAccountCount,
  forgetCachedAccounts,
  getActiveAccount,
  initMsal,
  isMsalConfigured,
  renewSignInByRedirect,
  signIn,
  signOut,
  signOutOfMicrosoft,
} from './msal'

export type AuthMode = 'teams' | 'browser'

let mode: AuthMode | null = null
let cachedToken: string | null = null
let cachedTokenExpiry = 0

/** Detect the host once. Cheap to call repeatedly afterwards. */
export async function detectHost(): Promise<AuthMode> {
  if (mode) return mode
  try {
    await app.initialize()
    mode = 'teams'
  } catch {
    mode = 'browser'
    if (isMsalConfigured) {
      await initMsal()
      await resolveConflictingSessions()
    }
  }
  return mode
}

/** A browser holding BOTH a cached Microsoft account and an email-code session is ambiguous, so neither is used.
 *  ⛔ Neither can win: both are valid credentials for different people and nothing says who is at the keyboard, so any choice serves one person's data to the other. Clear both; one forced sign-in is the price.
 *  `hasAnyBrowserSession()` then reads false and the App.tsx gate renders <SignIn>. */
async function resolveConflictingSessions(): Promise<void> {
  if (!hasOtpSession()) return
  if (cachedAccountCount() === 0) return

  console.warn(
    '[auth] this browser holds both a Microsoft account and a code session; ' +
      'clearing both rather than guessing which person is signed in (item 543)',
  )
  // ⛔ The code session is ended by a network call, awaited: deleting the flag cookie locally would not end it (the HttpOnly cookie keeps being sent),
  // leaving the browser signed in as the identity this function exists to discard. `endOtpSession()` clears the flag in a `finally`, so the gate falls to <SignIn> even if the call fails.
  await endOtpSession()
  await forgetCachedAccounts()
}

/** Decode a JWT's `exp` without verifying it; the signature is the server's business. A malformed token means "refresh now". */
function expiryOf(token: string): number {
  try {
    // JWT segments are base64url and atob() only accepts base64: normalize first, or atob throws on most real tokens and the 60s refresh cache never engages.
    const segment = token.split('.')[1] ?? ''
    const base64 = segment.replace(/-/g, '+').replace(/_/g, '/').padEnd(
      segment.length + ((4 - (segment.length % 4)) % 4),
      '=',
    )
    const payload = JSON.parse(atob(base64))
    return typeof payload.exp === 'number' ? payload.exp * 1000 : 0
  } catch {
    return 0
  }
}

/** Current bearer token, refreshed when close to expiring (60s margin). MSAL does its own caching in the browser.
 *  `fresh` skips this module's Teams cache so the retry after a 401 is not handed the token that was just refused. */
export async function getToken({ fresh = false }: { fresh?: boolean } = {}): Promise<string | null> {
  const host = await detectHost()

  if (host === 'browser') {
    // Microsoft first, then the email-code session; both are real production credentials. Keep both halves silent: the gate does the asking.
    // ⛔ `allowSilentSso` is why the order is safe: `ssoSilent` redeems whatever Entra cookie is around and would hand back the LAST Microsoft user's token ahead of the code session of the person at the keyboard,
    // so while a code session is live (`hasOtpSession()`, synchronous) that fallback is off and the explicit credential wins.
    const token = await acquireApiToken({ allowSilentSso: !hasOtpSession(), fresh })
    if (token) return token
    // Null means no HEADER credential: a live code session rides on the cookie and `api.ts` sends no Authorization header.
    return null
  }

  if (fresh) {
    cachedToken = null
    cachedTokenExpiry = 0
  }
  if (cachedToken && Date.now() < cachedTokenExpiry - 60_000) {
    return cachedToken
  }

  try {
    const token = await authentication.getAuthToken()
    cachedToken = token
    cachedTokenExpiry = expiryOf(token)
    return token
  } catch (error) {
    // Consent not granted or the tab misconfigured: surfaced by the caller, since a silent null looks like "logged out".
    console.error('[teams] getAuthToken failed', error)
    return null
  }
}

/** Whether a browser user has an active Microsoft account in this tab. */
export function hasBrowserAccount(): boolean {
  return getActiveAccount() !== null
}

// The email-code session.
// ⛔ The token is not readable here: it travels in an HttpOnly cookie set by the server (`api/deps.py:set_otp_cookies`), so XSS cannot take a 90-day credential.
// A companion non-HttpOnly flag cookie carries the one non-secret bit App.tsx's gate needs synchronously during render.
// ⛔ Deleting the flag does NOT end the session: the browser keeps sending the HttpOnly cookie until the SERVER clears it. See `endOtpSession()`.

/** Must match `OTP_FLAG_COOKIE_NAME` in `backend/trg_digital/api/deps.py`. */
const OTP_FLAG_COOKIE = 'trg_session_present'

/** Where the API lives; empty when it shares an origin with the site (`.env.example`). */
const API_BASE = import.meta.env.VITE_API_BASE_URL ?? ''

/** Is an email-code session live in this browser? Reads the flag cookie, never the token. Synchronous because callers cannot await.
 *  A HINT, not an authorisation: every request is authenticated server-side, and a stale flag resolves on the first 401. */
export function hasOtpSession(): boolean {
  if (typeof document === 'undefined') return false
  return document.cookie.split(';').some((part) => part.trim().startsWith(`${OTP_FLAG_COOKIE}=`))
}

/** Expire the flag locally. ⛔ Does NOT end the session; see `endOtpSession()`. */
function clearOtpFlag(): void {
  if (typeof document === 'undefined') return
  document.cookie = `${OTP_FLAG_COOKIE}=; Max-Age=0; Path=/; SameSite=Lax`
}

/** End the email-code session (the server half of sign-out).
 *  ⛔ A network call that can fail: the cookie is HttpOnly, so if the server is not reached the browser keeps a live credential. The flag is cleared in a `finally` so the browser drops to the sign-in gate regardless.
 *  Uses `fetch`, not the shared axios client: `api.ts` imports this module (cycle), and logout must not pass through `retryOnce401`, which would recurse on a 401. */
export async function endOtpSession(): Promise<void> {
  try {
    await fetch(`${API_BASE}/api/auth/logout`, { method: 'POST', credentials: 'include' })
  } catch (error) {
    // Logged, not thrown: sign-out must not leave the user stuck, and the session is capped at 90 days. Loud, because a browser that cannot reach logout holds a credential it believes it discarded.
    console.error('[auth] logout request failed; the session may still be live server-side', error)
  } finally {
    clearOtpFlag()
  }
}

/** True when signed in by either route. */
export function hasAnyBrowserSession(): boolean {
  return hasBrowserAccount() || hasOtpSession()
}

// The two explicit sign-ins: the most recent explicit sign-in wins, so each clears the OTHER credential, which keeps the two mutually exclusive.
// ⛔ Both live here, not in SignIn.tsx: vitest runs in node with no DOM, so an invariant in a component handler cannot be tested.

/** Take on the email-code session from `verify-code` as the one identity this browser is signed in as.
 *  ⛔ Throws rather than signing somebody in over a cached account: if the Microsoft account cannot be cleared, `getToken()` would renew it and show its owner's data to the person who typed a code. */
export async function adoptOtpSession(): Promise<void> {
  if (isMsalConfigured && !(await forgetCachedAccounts())) {
    // ⛔ Do not delete: the server already wrote the code-session cookie on the `verify-code` response, so throwing alone would leave the ambiguous two-credential browser.
    // End the session server-side before the throw, so a refusal leaves a browser nobody is wrongly signed into.
    await endOtpSession()
    throw new Error(
      'A Microsoft account is still cached in this browser and could not be ' +
        'cleared, so signing in by code here would not be safe. Sign out of ' +
        'Microsoft in this browser, or use a private window, and try again.',
    )
  }
  // Nothing to store on success (the cookie and its flag arrived with `verify-code`); this is purely the conflict guard, so it must still be awaited.
}

/** At the sign-in gate: renew a cached Microsoft account by a no-typing redirect when silent renewal needs Microsoft. True when the page is navigating away. */
export async function resumeMicrosoftSession(): Promise<boolean> {
  if ((await detectHost()) !== 'browser') return false
  return renewSignInByRedirect(hasOtpSession())
}

/** Start the interactive Microsoft sign-in as the one identity this browser is signed in as.
 *  Clear first: `signIn()` navigates away and never returns, so a stale long-lived code session would survive the redirect. */
export async function signInWithMicrosoft(): Promise<void> {
  // Awaited: the code session can only be ended by the server, so that round trip must complete before we leave.
  await endOtpSession()
  await signIn()
}

/** The one sign-out, for either credential, local only (the Microsoft session elsewhere stays signed in): code session first, then the cached MSAL account. Pinned in msal.test.ts. */
export async function signOutEverywhere(): Promise<void> {
  await endOtpSession()
  await signOut()
}

/** Plain sign-out that lands on the sign-in page at once, failure included: nothing else re-checks auth until a later request. */
export async function signOutAndLand(): Promise<void> {
  try {
    await signOutEverywhere()
  } catch {
    // Fail closed: the reload re-reads whatever credential survived, so landing is never worse than staying.
  } finally {
    window.location.replace(window.location.origin)
  }
}

/** Sign-out that also ends the Microsoft session: code session first, since the redirect never returns. */
export async function signOutOfMicrosoftToo(): Promise<void> {
  await endOtpSession()
  await signOutOfMicrosoft()
}

/** Teams theme -> our light/dark class, kept in sync as the user switches. */
export async function watchTheme(apply: (theme: string) => void): Promise<void> {
  if ((await detectHost()) !== 'teams') return
  const context = await app.getContext()
  apply(context.app.theme)
  app.registerOnThemeChangeHandler(apply)
}
